import type { ReactNode } from 'react';

import {
  Combobox as BaseCombobox,
  type ComboboxInputGroupProps,
  type ComboboxInputProps,
  type ComboboxPrimitiveValue,
} from '@base-ui/react';
import { useTranslation } from 'react-i18next';

import { clsxs } from '~/styles/utils';

import { Icon, type IconName } from '../../Icon/Icon';
import { adornmentStyle, controlStateTarget, controlThemeStyle, prefixIconStyle } from '../form.css';
import { useAccessibleAffixes } from '../form.hooks';
import { chipRemoveStyle, chipsStyle, chipStyle, clearStyle, inputGroupStyle, inputStyle, triggerStyle } from './ComboboxButton.css';

type Props = ComboboxInputGroupProps &
  Pick<ComboboxInputProps, 'placeholder'> & {
    adornment?: ReactNode;
    clearable?: boolean;
    icon?: IconName;
    prefix?: string;
    suffix?: string;
  };

export const ComboboxButton = ({ adornment, clearable = true, icon, placeholder, prefix, suffix, ...delegated }: Props) => {
  const { t } = useTranslation(['common']);
  const { ariaDescribedBy, prefixProps, suffixProps } = useAccessibleAffixes({ prefix, suffix });

  return (
    <div className={controlThemeStyle}>
      <BaseCombobox.InputGroup {...delegated} className={clsxs(controlStateTarget, inputGroupStyle, delegated.className)}>
        {icon && <Icon className={prefixIconStyle} name={icon} />}

        {adornment && <span className={adornmentStyle}>{adornment}</span>}

        {prefix && <span {...prefixProps}>{prefix}</span>}

        <BaseCombobox.Input
          className={inputStyle}
          placeholder={placeholder}
          render={(inputProps) => (
            <input
              {...inputProps}
              aria-describedby={[inputProps['aria-describedby'], ariaDescribedBy].filter(Boolean).join(' ') || undefined}
            />
          )}
        />

        {clearable && (
          <BaseCombobox.Clear aria-label={t('common:actions.clear')} className={clearStyle}>
            <Icon name="CloseSmall" />
          </BaseCombobox.Clear>
        )}

        <BaseCombobox.Trigger className={triggerStyle}>
          <Icon name="ChevronDown" />
        </BaseCombobox.Trigger>

        {suffix && <span {...suffixProps}>{suffix}</span>}
      </BaseCombobox.InputGroup>
    </div>
  );
};

type ChipsProps<Value extends ComboboxPrimitiveValue> = Omit<Props, 'clearable' | 'prefix' | 'suffix'> & {
  /** Necessary because Base UI only passes the selected values to the chips, not their items: https://github.com/mui/base-ui/issues/6098 */
  getLabel: (value: Value) => string;
};

export const ComboboxChipsButton = <Value extends ComboboxPrimitiveValue>({
  adornment,
  getLabel,
  icon,
  placeholder,
  ...delegated
}: ChipsProps<Value>) => {
  const { t } = useTranslation(['common']);

  return (
    <div className={controlThemeStyle}>
      <BaseCombobox.InputGroup {...delegated} className={clsxs(controlStateTarget, inputGroupStyle, delegated.className)}>
        {icon && <Icon className={prefixIconStyle} name={icon} />}

        {adornment && <span className={adornmentStyle}>{adornment}</span>}

        <BaseCombobox.Chips className={chipsStyle}>
          <BaseCombobox.Value>
            {(values: Value[]) => (
              <>
                {values.map((value) => (
                  <BaseCombobox.Chip className={chipStyle} key={String(value)}>
                    {getLabel(value)}

                    <BaseCombobox.ChipRemove
                      aria-label={t('common:combobox.removeChip', { label: getLabel(value) })}
                      className={chipRemoveStyle}
                    >
                      <Icon name="CloseSmall" size={20} />
                    </BaseCombobox.ChipRemove>
                  </BaseCombobox.Chip>
                ))}
                <BaseCombobox.Input className={inputStyle} placeholder={values.length === 0 ? placeholder : undefined} />
              </>
            )}
          </BaseCombobox.Value>
        </BaseCombobox.Chips>

        <BaseCombobox.Trigger className={triggerStyle}>
          <Icon name="ChevronDown" />
        </BaseCombobox.Trigger>
      </BaseCombobox.InputGroup>
    </div>
  );
};
